import React, { useState, useEffect } from 'react';
import { Company, Project, AgentResult, Interaction, CompanyMemory } from '../types';
import { MemoryService } from '../services/memoryService';
import { XIcon, PencilIcon, MailIcon, PhoneIcon, WhatsappIcon, GlobeIcon, TargetIcon, CheckIcon, BookIcon, RefreshIcon, LightbulbIcon, WarningIcon, UploadIcon, SearchIcon } from './Icons';

const translateCallPhrase = (text?: string) => {
    if (!text) return '';
    return text
        .replace(/^\[(Test Call|Live Call)\]\s*/gi, '')
        .replace(/Callback requested in 10 mins\./gi, 'Terugbelverzoek over 10 minuten.')
        .replace(/Error during generation/gi, 'Fout tijdens genereren')
        .replace(/Call simulation failed\./gi, 'Gespreksimulatie mislukt.')
        .replace(/\bBusy\b/gi, 'Bezet')
        .replace(/\bRecall soon\b/gi, 'Binnenkort terugbellen');
};

const normalizeTranscriptLine = (line: string) => {
    const trimmed = translateCallPhrase(line.trim());
    if (!trimmed) return '';
    return trimmed
        .replace(/^MODEL:/i, 'BDAT:')
        .replace(/^AGENT:/i, 'BDAT:')
        .replace(/^USER:/i, 'Contactpersoon:')
        .replace(/^CONTACT:/i, 'Contactpersoon:');
};

const formatCallTranscript = (details: any) => {
    if (!details) return '';
    if (typeof details === 'string') {
        return details
            .split(/\r?\n/)
            .map(normalizeTranscriptLine)
            .filter(Boolean)
            .join('\n');
    }
    if (Array.isArray(details?.transcript)) {
        return details.transcript
            .map((part: any) => {
                const speaker = part?.speaker === 'contact'
                    ? 'Contactpersoon'
                    : part?.speaker === 'user'
                        ? 'Contactpersoon'
                        : 'BDAT';
                return `${speaker}: ${translateCallPhrase(part?.text || '')}`.trim();
            })
            .filter((line: string) => line !== 'BDAT:' && line !== 'Contactpersoon:')
            .join('\n');
    }
    if (typeof details?.transcript === 'string') {
        return details.transcript
            .split(/\r?\n/)
            .map(normalizeTranscriptLine)
            .filter(Boolean)
            .join('\n');
    }
    if (typeof details?.rawTranscript === 'string') {
        return details.rawTranscript
            .split(/\r?\n/)
            .map(normalizeTranscriptLine)
            .filter(Boolean)
            .join('\n');
    }
    return '';
};

const formatCallAnalysis = (interaction: Interaction) => {
    const details = interaction.details;
    if (!details || typeof details === 'string') return '';

    const analysisLines: string[] = [];
    const summary = translateCallPhrase(details.summary || interaction.summary);
    if (summary) analysisLines.push(`Samenvatting: ${summary}`);

    if (details.outcome) {
        analysisLines.push(`Uitkomst: ${translateCallPhrase(details.outcome)}`);
    }

    if (Array.isArray(details.objections) && details.objections.length > 0) {
        analysisLines.push(`Bezwaren: ${details.objections.map((item: string) => translateCallPhrase(item)).join(', ')}`);
    }

    if (Array.isArray(details.nextSteps) && details.nextSteps.length > 0) {
        analysisLines.push(`Vervolgstappen: ${details.nextSteps.map((item: string) => translateCallPhrase(item)).join(', ')}`);
    }

    if (Array.isArray(details.coachingNotes) && details.coachingNotes.length > 0) {
        analysisLines.push(`AI leerpunten: ${details.coachingNotes.map((item: string) => translateCallPhrase(item)).join(', ')}`);
    }

    if (typeof details.score === 'number') {
        analysisLines.push(`Score: ${details.score}/100`);
    }

    return analysisLines.join('\n');
};

const getCallLearningBlocks = (details: any) => {
    if (!details || typeof details === 'string') {
        return {
            successfulAngles: [] as string[],
            failedAngles: [] as string[],
            commonObjections: [] as string[],
            factualNotes: [] as string[],
            coachingNotes: [] as string[],
        };
    }

    return {
        successfulAngles: Array.isArray(details.successfulAngles) ? details.successfulAngles.map((item: string) => translateCallPhrase(item)) : [],
        failedAngles: Array.isArray(details.failedAngles) ? details.failedAngles.map((item: string) => translateCallPhrase(item)) : [],
        commonObjections: Array.isArray(details.commonObjections) ? details.commonObjections.map((item: string) => translateCallPhrase(item)) : [],
        factualNotes: Array.isArray(details.factualNotes) ? details.factualNotes.map((item: string) => translateCallPhrase(item)) : [],
        coachingNotes: Array.isArray(details.coachingNotes) ? details.coachingNotes.map((item: string) => translateCallPhrase(item)) : [],
    };
};

const formatOutcomeLabel = (outcome?: string) => {
    switch ((outcome || '').toLowerCase()) {
        case 'positief_vervolg':
            return 'Positief vervolg';
        case 'afwijzing':
            return 'Afwijzing';
        case 'informatiegesprek':
            return 'Informatiegesprek';
        case 'onduidelijk':
            return 'Onduidelijk';
        default:
            return outcome ? translateCallPhrase(outcome) : 'Onbekend';
    }
};

const buildCallAnalytics = (history: Interaction[]) => {
    const calls = history.filter((item) => item.type === 'call');
    const validScores = calls
        .map((item) => item.details?.score)
        .filter((value) => typeof value === 'number');

    const sentiments = calls.reduce((acc, item) => {
        const key = item.sentiment || 'neutral';
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    const outcomes = calls.reduce((acc, item) => {
        const key = formatOutcomeLabel(item.details?.outcome);
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    const countFrequency = (values: string[]) => {
        const bucket = new Map<string, number>();
        values.forEach((value) => {
            const clean = value.trim();
            if (!clean) return;
            bucket.set(clean, (bucket.get(clean) || 0) + 1);
        });
        return Array.from(bucket.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([label, count]) => ({ label, count }));
    };

    const objections = countFrequency(
        calls.flatMap((item) => Array.isArray(item.details?.commonObjections) ? item.details.commonObjections : Array.isArray(item.details?.objections) ? item.details.objections : [])
    );
    const successfulAngles = countFrequency(
        calls.flatMap((item) => Array.isArray(item.details?.successfulAngles) ? item.details.successfulAngles : [])
    );
    const coachingNotes = countFrequency(
        calls.flatMap((item) => Array.isArray(item.details?.coachingNotes) ? item.details.coachingNotes : [])
    );
    const nextSteps = countFrequency(
        calls.flatMap((item) => Array.isArray(item.details?.nextSteps) ? item.details.nextSteps : [])
    );

    return {
        totalCalls: calls.length,
        transcriptCoverage: calls.filter((item) => formatCallTranscript(item.details)).length,
        avgScore: validScores.length ? Math.round(validScores.reduce((sum, score) => sum + score, 0) / validScores.length) : null,
        lastCall: calls[0] || null,
        sentiments,
        outcomes,
        objections: objections.slice(0, 4),
        successfulAngles: successfulAngles.slice(0, 4),
        coachingNotes: coachingNotes.slice(0, 4),
        nextSteps: nextSteps.slice(0, 3),
    };
};

interface CompanyDetailViewProps {
    company: Company;
    project: Project;
    onClose: () => void;
    onRefresh: () => void;
    memoryRefreshKey?: number;
    researchResults: AgentResult[];
    onUpdate: (c: Company) => void;
    onRunAgent?: (agentId: string, options: any) => void;
    onStartCall?: (company: Company) => void;
    onImportDossier?: (dossier: any) => void; // New prop for handling import in App state
    disableCallAgent?: boolean;
}

export const CompanyDetailView = ({ company, project, onClose, onRefresh, memoryRefreshKey = 0, researchResults, onUpdate, onRunAgent, onStartCall, onImportDossier, disableCallAgent }: CompanyDetailViewProps) => {
    const [tab, setTab] = useState<'info' | 'research' | 'history'>('info');
    const [isEditing, setIsEditing] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [formData, setFormData] = useState<Company>(company);

    // Memory State
    const [history, setHistory] = useState<Interaction[]>([]);
    const [patterns, setPatterns] = useState<CompanyMemory['patterns'] | null>(null);
    const [editingInteraction, setEditingInteraction] = useState<string | null>(null);
    const [editInteractionData, setEditInteractionData] = useState<string>('');
    const [isEditingPatterns, setIsEditingPatterns] = useState(false);
    const [loadingAction, setLoadingAction] = useState(false);

    const loadMemory = async () => {
        const mem = await MemoryService.syncMemoryFromBackend(company.id, project.id);
        setHistory([...mem.history]);
        setPatterns({ ...mem.patterns });
    };

    // Load initial data and hydrate if needed
    useEffect(() => {
        const currentCompany = { ...company };
        const research = researchResults.find(r => r.agentId === 'researcher' && (r.data.companyId === company.id || r.data.companyName === company.name));

        let needsSync = false;
        if (research) {
            const info = research.data.contactInfo || {};
            if (!currentCompany.email && info.email) { currentCompany.email = info.email; needsSync = true; }
            if (!currentCompany.phone && info.phone) { currentCompany.phone = info.phone; needsSync = true; }
            if (!currentCompany.website && info.website) { currentCompany.website = info.website; needsSync = true; }
            if (!currentCompany.location && info.address) { currentCompany.location = info.address; needsSync = true; }
            if (!currentCompany.contactName && info.keyContact) { currentCompany.contactName = info.keyContact; needsSync = true; }
            if ((!currentCompany.description || currentCompany.description.length < 5) && research.data.summary) { currentCompany.description = research.data.summary; needsSync = true; }
        }

        if (needsSync) {
            onUpdate(currentCompany);
        }

        setFormData(currentCompany);
        void loadMemory();
    }, [company.id, researchResults, project.id, memoryRefreshKey]);

    const handleSave = () => {
        onUpdate(formData);
        setIsEditing(false);
    };

    const handleSaveInteraction = (id: string) => {
        if (!editingInteraction) return;
        MemoryService.updateInteraction(company.id, project.id, id, editInteractionData);
        void loadMemory();
        setEditingInteraction(null);
    };

    const handleSavePatterns = () => {
        if (!patterns) return;
        MemoryService.updatePatterns(company.id, project.id, patterns);
        setIsEditingPatterns(false);
        void loadMemory();
    };

    const getSpecificResult = (type: string) => researchResults.find(r => r.agentId === type && (r.data.companyName === company.name || r.data.companyId === company.id));
    const competitorResult = researchResults.find(r => (r.agentId === 'researcher-competitor' || (r.agentId === 'researcher' && r.data.marketPosition)) && (r.data.companyName === company.name || r.data.companyId === company.id));
    const printableCallHistory = history.filter(h => h.type === 'call');
    const callAnalytics = React.useMemo(() => buildCallAnalytics(history), [history]);
    const hasResearchSection = !!getSpecificResult('researcher');
    const hasCompetitorSection = !!competitorResult;
    const hasStrategySection = !!getSpecificResult('strategy-agent');
    const hasPatternsSection = !!(patterns && (patterns.successfulAngles.length > 0 || patterns.failedAngles.length > 0));
    const hasCallSection = printableCallHistory.length > 0;

    const handleExport = () => {
        const research = researchResults.filter(r => r.data.companyId === company.id || r.data.companyName === company.name);
        const dossier = MemoryService.exportDossier(company.id, project.id, company, research);
        const dataStr = JSON.stringify(dossier, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

        const projectNaam = project.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const bedrijfNaam = company.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const exportNaam = `${projectNaam}_-_${bedrijfNaam}_dossier.json`;

        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportNaam);
        linkElement.click();
    };

    const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const json = JSON.parse(event.target?.result as string);
                const { memory, tasks } = MemoryService.importDossier(json, project.id);
                // Trigger refresh
                void loadMemory();
                if (onImportDossier) onImportDossier(json);
                alert("Dossier geïmporteerd en samengevoegd!");
            } catch (err) {
                console.error(err);
                alert("Fout bij importeren. Ongeldig dossier bestand.");
            }
        };
        reader.readAsText(file);
    };

    const [isPrinting, setIsPrinting] = useState(false);
    const [isPreparingPrint, setIsPreparingPrint] = useState(false);
    const [printSections, setPrintSections] = useState({
        opdrachtgever: true,
        profiel: true,
        onderzoek: true,
        concurrentie: true,
        strategie: true,
        learnings: true,
        calls: true,
    });

    const getDefaultPrintSections = () => ({
        opdrachtgever: true,
        profiel: true,
        onderzoek: hasResearchSection,
        concurrentie: hasCompetitorSection,
        strategie: hasStrategySection,
        learnings: hasPatternsSection,
        calls: hasCallSection,
    });

    const openPrintPreview = () => {
        setPrintSections(getDefaultPrintSections());
        setIsPrinting(true);
    };

    const closePrintPreview = () => {
        setIsPrinting(false);
    };

    const togglePrintSection = (section: keyof typeof printSections) => {
        setPrintSections((current) => ({
            ...current,
            [section]: !current[section],
        }));
    };

    const selectAllPrintSections = () => {
        setPrintSections(getDefaultPrintSections());
    };

    const selectCallOnlyPrintSections = () => {
        setPrintSections({
            opdrachtgever: false,
            profiel: false,
            onderzoek: false,
            concurrentie: false,
            strategie: false,
            learnings: false,
            calls: hasCallSection,
        });
    };

    const printStyles = `
        @page {
            size: A4;
            margin: 8mm;
        }
        @media print {
            .bdat-print-layout,
            .bdat-print-layout * {
                box-sizing: border-box !important;
            }
            .bdat-print-layout {
                width: 100% !important;
                max-width: none !important;
                margin: 0 !important;
                padding: 0 !important;
                font-size: 10.25px !important;
                line-height: 1.38 !important;
            }
            .bdat-print-layout .bdat-print-full {
                width: 100% !important;
                max-width: none !important;
                margin-left: 0 !important;
                margin-right: 0 !important;
            }
            .bdat-print-layout .bdat-print-safe {
                overflow-wrap: anywhere !important;
                word-break: break-word !important;
            }
            .bdat-print-layout .bdat-print-stack {
                display: block !important;
                width: 100% !important;
                max-width: none !important;
            }
            .bdat-print-layout .bdat-print-stack > * {
                width: 100% !important;
                max-width: none !important;
            }
            .bdat-print-layout .bdat-print-grid-2 {
                display: grid !important;
                grid-template-columns: minmax(0, 1fr) !important;
                gap: 10px !important;
            }
            .bdat-print-layout .bdat-print-grid-3 {
                display: grid !important;
                grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
                gap: 10px !important;
            }
            .bdat-print-layout .bdat-print-compact {
                padding: 12px !important;
                border-radius: 14px !important;
            }
            .bdat-print-layout .bdat-print-page-break {
                break-before: page !important;
                page-break-before: always !important;
            }
            .bdat-print-layout .bdat-print-inline-header {
                display: grid !important;
                grid-template-columns: minmax(0, 1fr) 72px !important;
                gap: 14px !important;
                align-items: start !important;
            }
            .bdat-print-layout .bdat-print-inline-photo {
                width: 72px !important;
                height: 72px !important;
                margin: 0 !important;
                padding: 4px !important;
                justify-self: end !important;
                align-self: start !important;
            }
            .bdat-print-layout h1 {
                font-size: 24px !important;
                line-height: 1.05 !important;
            }
            .bdat-print-layout h2 {
                font-size: 15px !important;
                line-height: 1.2 !important;
            }
            .bdat-print-layout h4 {
                font-size: 11px !important;
                line-height: 1.3 !important;
            }
            .bdat-print-layout p,
            .bdat-print-layout li,
            .bdat-print-layout span {
                max-width: 100% !important;
            }
            .bdat-print-layout .bdat-print-grid-2 > *,
            .bdat-print-layout .bdat-print-grid-3 > * {
                min-width: 0 !important;
            }
            .print-toolbar {
                display: none !important;
            }
        }
    `;

    const openBrowserPrintDialog = (contentHtml: string) => {
        const printWindow = window.open('', '_blank', 'width=1200,height=900');
        if (!printWindow) {
            alert('Kon het printvenster niet openen. Sta pop-ups toe voor deze site.');
            return;
        }

        const assetTags = Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style'))
            .map((node) => node.outerHTML)
            .join('\n');
        const printTitle = `${project.name} - ${company.name} - BDAT Dossier`;

        printWindow.document.open();
        printWindow.document.write(`
            <!doctype html>
            <html lang="nl">
            <head>
                <meta charset="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <title>${printTitle}</title>
                ${assetTags}
                <style>
                    ${printStyles}
                    html, body {
                        margin: 0;
                        padding: 0;
                        background: #ffffff;
                    }
                    @media screen {
                        body {
                            background: #e5e7eb;
                            padding: 24px;
                        }
                        .bdat-print-layout {
                            position: static !important;
                            inset: auto !important;
                            max-width: 210mm !important;
                            margin: 0 auto !important;
                            overflow: visible !important;
                            background: #ffffff;
                            box-shadow: 0 18px 48px rgba(15, 23, 42, 0.15);
                        }
                    }
                </style>
            </head>
            <body>${contentHtml}</body>
            </html>
        `);
        printWindow.document.close();

        const runPrint = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.onafterprint = () => {
                printWindow.close();
            };
        };

        printWindow.addEventListener('load', () => setTimeout(runPrint, 150), { once: true });
    };

    const handlePrint = () => {
        const performPrint = () => {
            const layout = document.querySelector(isPrinting ? '.bdat-print-layout[data-print-source="preview"]' : '.bdat-print-layout[data-print-source="hidden"]') as HTMLElement | null;
            if (!layout) {
                setIsPrinting(false);
                setIsPreparingPrint(false);
                return;
            }
            openBrowserPrintDialog(layout.outerHTML);
            setIsPrinting(false);
            setIsPreparingPrint(false);
        };

        if (isPrinting) {
            performPrint();
            return;
        }

        setPrintSections(getDefaultPrintSections());
        setIsPreparingPrint(true);
        setTimeout(performPrint, 120);
    };

    return (
        <>
            {/* --- PRINTABLE VIEW (Only visible when printing) --- */}
            {(isPrinting || isPreparingPrint) && (
                <>
                    <style>{printStyles}</style>
                    <div
                        data-print-source={isPrinting ? 'preview' : 'hidden'}
                        className={`bdat-print-layout bg-white text-slate-900 p-6 print:relative print:inset-auto print:w-full print:overflow-visible print:p-0 ${
                            isPrinting
                                ? 'fixed inset-0 z-[9999] overflow-y-auto overscroll-contain'
                                : 'pointer-events-none fixed left-[-300vw] top-0 z-[-1] h-auto max-h-none w-[210mm] overflow-hidden opacity-0'
                        }`}
                    >
                    {isPrinting && (
                    <div className="print-toolbar sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-slate-200 bg-white/96 px-2 pb-4 pt-1 backdrop-blur print:hidden">
                        <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-black uppercase tracking-[0.28em] text-slate-400">Print Preview</p>
                            <p className="text-sm font-semibold text-slate-700">Scroll door alle pagina's en kies daarna pas Print / PDF.</p>
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                                <button
                                    onClick={selectAllPrintSections}
                                    className="rounded-full border border-slate-200 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
                                >
                                    Alles
                                </button>
                                <button
                                    onClick={selectCallOnlyPrintSections}
                                    className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-100"
                                >
                                    Alleen gesprek
                                </button>
                                {[
                                    { key: 'opdrachtgever', label: 'Opdrachtgever', enabled: true },
                                    { key: 'profiel', label: 'Profiel', enabled: true },
                                    { key: 'onderzoek', label: 'Onderzoek', enabled: hasResearchSection },
                                    { key: 'concurrentie', label: 'Concurrentie', enabled: hasCompetitorSection },
                                    { key: 'strategie', label: 'Strategie', enabled: hasStrategySection },
                                    { key: 'learnings', label: 'Learnings', enabled: hasPatternsSection },
                                    { key: 'calls', label: 'Calls', enabled: hasCallSection },
                                ].map((section) => (
                                    <button
                                        key={section.key}
                                        onClick={() => togglePrintSection(section.key as keyof typeof printSections)}
                                        disabled={!section.enabled}
                                        className={`rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] transition ${
                                            !section.enabled
                                                ? 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-300'
                                                : printSections[section.key as keyof typeof printSections]
                                                    ? 'border border-slate-900 bg-slate-900 text-white'
                                                    : 'border border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-800'
                                        }`}
                                    >
                                        {section.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                            <button
                                onClick={closePrintPreview}
                                className="rounded-full border border-slate-200 px-4 py-2 text-xs font-black uppercase tracking-[0.24em] text-slate-500 transition hover:border-slate-300 hover:text-slate-800"
                            >
                                Sluiten
                            </button>
                            <button
                                onClick={handlePrint}
                                className="rounded-full bg-slate-900 px-5 py-2.5 text-xs font-black uppercase tracking-[0.24em] text-white transition hover:bg-slate-700"
                            >
                                Print / PDF
                            </button>
                        </div>
                    </div>
                    )}
                    <div className="bdat-print-stack w-full max-w-none mx-auto space-y-6 pt-5 print:space-y-5 print:pt-0">

                        {/* BOVENKANT: Info van de Opdrachtgever */}
                        {printSections.opdrachtgever && (
                        <aside className="bdat-print-full w-full border-b-4 border-slate-100 pb-6 break-inside-avoid print:border-b-2 print:pb-5">
                            <div className={project.clientPhoto?.data ? 'bdat-print-inline-header' : ''}>
                                <div>
                                    <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-4">Opdrachtgever / Voor wie is dit?</p>
                                    <h2 className="bdat-print-safe text-3xl font-black text-slate-900 uppercase tracking-tight mb-2">{project.name}</h2>
                                    <p className="bdat-print-safe text-[14px] text-slate-700 font-medium leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100 italic whitespace-pre-wrap">
                                        {project.description || 'Geen projectbeschrijving beschikbaar.'}
                                    </p>
                                </div>

                                {project.clientPhoto?.data && (
                                    <div className="bdat-print-inline-photo rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 shadow-sm flex items-center justify-center">
                                        <img src={project.clientPhoto.data as string} alt="Opdrachtgever Logo/Foto" className="w-full h-full object-cover" />
                                    </div>
                                )}
                            </div>
                        </aside>
                        )}

                        {/* ONDERKANT: Info van de Gebelde Klant (Bedrijf) */}
                        <main className="bdat-print-full w-full space-y-6">
                            {/* Header */}
                            {(printSections.profiel || printSections.onderzoek || printSections.concurrentie || printSections.strategie || printSections.learnings || printSections.calls) && (
                            <div className="border-b-4 border-indigo-600 pb-5 mb-6 flex justify-between items-end gap-4">
                                <div className="min-w-0">
                                    <p className="text-xs font-black text-indigo-600 uppercase tracking-widest mb-2">Gebelde Klant / Lead</p>
                                    <h1 className="bdat-print-safe text-5xl font-black text-slate-900 uppercase tracking-tight">{formData.name}</h1>
                                    <p className="bdat-print-safe text-[15px] text-slate-500 font-medium mt-2">{formData.industry} • {formData.location}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-1">BDAT Dossier</p>
                                    <p className="text-sm text-slate-600 font-medium">{new Date().toLocaleDateString('nl-NL')}</p>
                                </div>
                            </div>
                            )}

                            {/* Profiel */}
                            {printSections.profiel && (
                            <section className="space-y-4">
                                <h2 className="text-xl font-black text-indigo-700 uppercase tracking-widest border-b-2 border-indigo-100 pb-2 flex items-center gap-2"><BookIcon /> Bedrijfsprofiel</h2>
                                <p className="bdat-print-safe text-[12.5px] text-slate-700 leading-relaxed font-medium whitespace-pre-wrap">{formData.description || 'Geen beschrijving beschikbaar.'}</p>
                                <div className="bdat-print-grid-3 bdat-print-compact grid grid-cols-1 sm:grid-cols-3 gap-6 mt-5 bg-slate-50 rounded-xl border border-slate-100 print:grid-cols-3">
                                    <div>
                                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Direct E-mail</p>
                                        <p className="bdat-print-safe text-[13px] font-bold text-slate-800">{formData.email || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Telefoonnummer</p>
                                        <p className="bdat-print-safe text-[13px] font-bold text-slate-800">{formData.phone || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Website</p>
                                        <p className="bdat-print-safe text-[13px] font-bold text-slate-800">{formData.website || '-'}</p>
                                    </div>
                                </div>
                            </section>
                            )}

                            {/* Onderzoek */}
                            {printSections.onderzoek && getSpecificResult('researcher') && (
                                <section className="bdat-print-page-break space-y-4 mt-10 break-inside-avoid">
                                    <h2 className="text-xl font-black text-indigo-700 uppercase tracking-widest border-b-2 border-indigo-100 pb-2 flex items-center gap-2"><TargetIcon /> Onderzoek Inzichten</h2>
                                    <div className="bdat-print-full bdat-print-compact bg-indigo-50/50 rounded-2xl border border-indigo-100">
                                        <p className="bdat-print-safe text-[12.5px] text-slate-800 leading-relaxed font-medium whitespace-pre-wrap">{getSpecificResult('researcher')?.data.summary}</p>
                                    </div>
                                    {getSpecificResult('researcher')?.data.deepInsights && (
                                        <div className="mt-6 pl-2">
                                            <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Diepte Inzichten</p>
                                            <ul className="space-y-3">
                                                {getSpecificResult('researcher')?.data.deepInsights.map((insight: string, i: number) => (
                                                    <li key={i} className="bdat-print-safe text-[11px] text-slate-700 flex gap-3 leading-relaxed">
                                                        <span className="w-2 h-2 bg-indigo-500 rounded-full mt-1.5 shrink-0"></span>
                                                        {insight}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </section>
                            )}

                            {/* Concurrentie */}
                            {printSections.concurrentie && competitorResult && (
                                <section className="bdat-print-page-break space-y-4 mt-10 break-inside-avoid">
                                    <h2 className="text-xl font-black text-orange-600 uppercase tracking-widest border-b-2 border-orange-100 pb-2 flex items-center gap-2"><TargetIcon /> Concurrentie Analyse</h2>
                                    <div className="bdat-print-compact bg-orange-50/50 rounded-2xl border border-orange-100">
                                        <p className="text-xs font-black text-orange-500 uppercase tracking-widest mb-2">Marktpositie</p>
                                        <p className="bdat-print-safe text-[12.5px] font-bold text-slate-800 mb-5 leading-relaxed">{competitorResult.data.marketPosition}</p>

                                        <p className="text-xs font-black text-orange-500 uppercase tracking-widest mb-3">Top Concurrenten</p>
                                        <div className="bdat-print-grid-2 grid grid-cols-1 gap-3" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                                            {competitorResult.data.topCompetitors?.map((comp: any, idx: number) => (
                                                <div key={idx} className="bdat-print-safe min-w-0 bg-white p-3 rounded-xl border border-orange-200 shadow-sm text-[10.5px]">
                                                    <span className="bdat-print-safe font-bold text-slate-900 block mb-2 text-[12px]">{comp.name}</span>
                                                    <p className="bdat-print-safe text-green-700 bg-green-50 px-3 py-1.5 rounded mb-2 border border-green-100 leading-[1.35]"><strong>Sterkte:</strong> <span className="block mt-1">{comp.strength}</span></p>
                                                    <p className="bdat-print-safe text-red-700 bg-red-50 px-3 py-1.5 rounded border border-red-100 leading-[1.35]"><strong>Zwakte:</strong> <span className="block mt-1">{comp.weakness}</span></p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Strategie */}
                            {printSections.strategie && getSpecificResult('strategy-agent') && (
                                <section className="bdat-print-page-break space-y-4 mt-10 break-inside-avoid">
                                    <h2 className="text-xl font-black text-emerald-600 uppercase tracking-widest border-b-2 border-emerald-100 pb-2 flex items-center gap-2"><LightbulbIcon /> Gekozen Strategie</h2>
                                    <div className="bdat-print-compact bg-emerald-50/50 rounded-2xl border border-emerald-100">
                                        <h4 className="bdat-print-safe text-[13px] font-black text-emerald-800 uppercase tracking-[0.12em] mb-3">{getSpecificResult('strategy-agent')?.data.strategyTitle}</h4>
                                        <p className="bdat-print-safe text-[12.5px] text-slate-800 leading-relaxed font-medium italic mb-5">"{getSpecificResult('strategy-agent')?.data.approachAngle}"</p>

                                        <p className="text-xs font-black text-emerald-600 uppercase tracking-widest mb-3">Key Pitch Points</p>
                                        <ul className="bdat-print-safe list-disc pl-5 text-[11px] font-bold text-emerald-900 space-y-2">
                                            {getSpecificResult('strategy-agent')?.data.keyPitchPoints?.map((p: string, i: number) => (
                                                <li key={i}>{p}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </section>
                            )}

                            {/* Geheugen / Patronen */}
                            {printSections.learnings && patterns && (patterns.successfulAngles.length > 0 || patterns.failedAngles.length > 0) && (
                                <section className="space-y-4 mt-10 break-inside-avoid">
                                    <h2 className="text-xl font-black text-slate-700 uppercase tracking-widest border-b-2 border-slate-200 pb-2 flex items-center gap-2"><BookIcon /> Succes & Faalfactoren</h2>
                                    <div className="bdat-print-grid-2 grid grid-cols-1 gap-3" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                                        <div className="bdat-print-compact bg-green-50/50 rounded-xl border border-green-100">
                                            <p className="text-xs font-black text-green-600 uppercase tracking-widest mb-3">Wat werkt (Succes)</p>
                                            <ul className="bdat-print-safe list-disc pl-5 text-[11px] font-medium text-slate-800 space-y-2">
                                                {patterns.successfulAngles.map((p, i) => <li key={i}>{p}</li>)}
                                            </ul>
                                        </div>
                                        <div className="bdat-print-compact bg-red-50/50 rounded-xl border border-red-100">
                                            <p className="text-xs font-black text-red-600 uppercase tracking-widest mb-3">Wat werkt NIET (Falen)</p>
                                            <ul className="bdat-print-safe list-disc pl-5 text-[11px] font-medium text-slate-800 space-y-2">
                                                {patterns.failedAngles.map((p, i) => <li key={i}>{p}</li>)}
                                            </ul>
                                        </div>
                                    </div>
                                </section>
                            )}

                            {/* Alleen calls met transcript / AI analyse in print */}
                            {printSections.calls && printableCallHistory.length > 0 && (
                                <section className="bdat-print-page-break space-y-4 mt-10">
                                    <h2 className="text-xl font-black text-slate-700 uppercase tracking-widest border-b-2 border-slate-200 pb-2 flex items-center gap-2">
                                        <PhoneIcon /> Call Transcripten & AI Analyse
                                    </h2>
                                    <div className="space-y-6">
                                        {printableCallHistory.map((h) => {
                                            const transcript = formatCallTranscript(h.details);
                                            const analysis = formatCallAnalysis(h);

                                            return (
                                                <div key={h.id} className="bdat-print-compact bg-slate-50 rounded-2xl border border-slate-200 break-inside-avoid">
                                                    <div className="flex justify-between items-start gap-4 mb-4">
                                                        <div className="min-w-0">
                                                            <h4 className="text-lg font-black text-indigo-900 capitalize flex items-center gap-3">
                                                                <PhoneIcon />
                                                                Call <span className="text-slate-500 font-normal uppercase tracking-widest text-xs ml-2">({h.direction === 'outbound' ? 'uitgaand' : 'inkomend'})</span>
                                                            </h4>
                                                            <p className="bdat-print-safe text-[14px] text-slate-800 mt-3 font-medium bg-white p-4 rounded-xl border border-slate-100 italic leading-relaxed">
                                                                {translateCallPhrase(h.summary)}
                                                            </p>
                                                        </div>
                                                        <span className="text-xs font-bold text-slate-600 bg-white px-3 py-1.5 rounded shadow-sm border border-slate-100 shrink-0">
                                                            {new Date(h.timestamp).toLocaleString('nl-NL')}
                                                        </span>
                                                    </div>

                                                    {transcript && (
                                                        <div className="mt-5">
                                                            <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Besproken Transcriptie</p>
                                                            <div className="bdat-print-safe p-4 bg-white rounded-xl text-[10.5px] font-mono text-slate-800 whitespace-pre-wrap border border-slate-200 shadow-inner leading-relaxed">
                                                                {transcript}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {analysis && (
                                                        <div className="mt-5">
                                                            <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-3">AI Analyse Van Het Gesprek</p>
                                                            <div className="bdat-print-safe p-4 bg-white rounded-xl text-[10.5px] text-slate-800 whitespace-pre-wrap border border-slate-200 shadow-inner leading-relaxed">
                                                                {analysis}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            )}

                            <div className="pt-10 text-center text-xs font-bold text-slate-400 border-t border-slate-200 mt-10 uppercase tracking-widest">
                                Geautomatiseerd Gegenereerd door BDAT Autonomous Intelligence - {new Date().toLocaleDateString('nl-NL')}
                            </div>
                        </main>
                    </div>
                </div>
                </>
            )}

            {/* --- NORMAL VIEW --- */}
            <div className={`fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-white/10 bg-brand-navy shadow-2xl transform transition-transform duration-300 print:hidden md:left-[var(--bdat-sidebar-width)] md:w-auto md:max-w-[calc(100vw-var(--bdat-sidebar-width))] lg:max-w-[min(960px,calc(100vw-var(--bdat-sidebar-width)))] ${isPrinting ? 'translate-x-[110%]' : 'translate-x-0'}`}>
                <div className="shrink-0 border-b border-white/10 bg-brand-slate/50 px-4 py-4 lg:p-5 flex flex-wrap justify-between items-center gap-3">
                    <div className="min-w-0">
                        <h2 className="text-lg lg:text-xl font-bold text-white line-clamp-1">{formData.name}</h2>
                        <p className="text-[11px] lg:text-xs text-slate-400 line-clamp-1">{formData.industry} • {formData.location}</p>
                    </div>
                    <div className="flex items-center gap-1 lg:gap-2 shrink-0">
                        <button onClick={openPrintPreview} className="p-2 text-slate-500 hover:text-white transition-colors" title="Afdrukken naar PDF">
                            <i className="fas fa-print"></i>
                        </button>
                        <button onClick={handleExport} className="p-2 text-slate-500 hover:text-white transition-colors" title="Dossier Exporteren (JSON)">
                            <i className="fas fa-download"></i>
                        </button>
                        <button onClick={() => fileInputRef.current?.click()} className="p-2 text-slate-500 hover:text-white transition-colors" title="Dossier Importeren (JSON)">
                            <UploadIcon />
                        </button>
                        <input type="file" ref={fileInputRef} className="hidden" accept=".json" onChange={handleImport} />
                        <div className="w-px h-6 bg-white/10 mx-2"></div>
                        <button onClick={onClose} className="p-2 text-slate-500 hover:text-white transition-colors"><XIcon /></button>
                    </div>
                </div>

                <div className="flex shrink-0 border-b border-white/10 bg-brand-navy">
                    <button onClick={() => setTab('info')} className={`flex-1 px-2 py-3 text-[10px] lg:text-xs font-bold uppercase tracking-[0.16em] ${tab === 'info' ? 'text-brand-purple border-b-2 border-brand-purple bg-brand-purple/10' : 'text-slate-500 hover:text-white'}`}>Profiel</button>
                    <button onClick={() => setTab('research')} className={`flex-1 px-2 py-3 text-[10px] lg:text-xs font-bold uppercase tracking-[0.16em] ${tab === 'research' ? 'text-brand-purple border-b-2 border-brand-purple bg-brand-purple/10' : 'text-slate-500 hover:text-white'}`}>Dossier Inzichten</button>
                    <button onClick={() => setTab('history')} className={`flex-1 px-2 py-3 text-[10px] lg:text-xs font-bold uppercase tracking-[0.16em] ${tab === 'history' ? 'text-brand-purple border-b-2 border-brand-purple bg-brand-purple/10' : 'text-slate-500 hover:text-white'}`}>Geheugen</button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto px-4 py-6 lg:p-8 bg-brand-navy pb-28 lg:pb-32 custom-scrollbar">
                    {tab === 'info' && (
                        <div className="space-y-8">
                            <div className="flex justify-between items-center border-b border-white/10 pb-4">
                                <h3 className="font-black text-white text-[10px] uppercase tracking-[0.2em]">Stamgegevens</h3>
                                <div className="flex gap-4">
                                    {!isEditing ? (
                                        <button onClick={() => setIsEditing(true)} className="text-[10px] text-brand-purple flex items-center gap-1 font-bold hover:bg-white/5 px-2 py-1 rounded transition-all"><PencilIcon /> Bewerken</button>
                                    ) : (
                                        <div className="flex gap-3">
                                            <button onClick={() => setIsEditing(false)} className="text-[10px] text-slate-400 font-bold hover:text-white">Annuleren</button>
                                            <button onClick={handleSave} className="text-[10px] text-emerald-500 font-bold flex items-center gap-1 hover:text-emerald-400"><CheckIcon /> Opslaan</button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-5">
                                <div>
                                    <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2">Beschrijving</label>
                                    {isEditing ? <textarea className="w-full border border-white/10 p-3 rounded-xl text-sm bg-brand-slate text-white focus:ring-2 focus:ring-brand-purple outline-none h-32" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} /> : <p className="text-sm text-slate-300 leading-relaxed bg-white/5 p-4 rounded-xl border border-white/5">{formData.description}</p>}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2">Direct E-mail</label>
                                        {isEditing ? <input className="w-full border border-white/10 p-3 rounded-xl text-sm bg-brand-slate text-white focus:ring-2 focus:ring-brand-purple" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} /> : <p className="text-sm text-slate-200 font-medium">{formData.email || 'Niet ingevuld'}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2">Telefoonnummer</label>
                                        {isEditing ? <input className="w-full border border-white/10 p-3 rounded-xl text-sm bg-brand-slate text-white focus:ring-2 focus:ring-brand-purple" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} /> : <p className="text-sm text-slate-200 font-medium">{formData.phone || 'Niet ingevuld'}</p>}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-2">Website</label>
                                    {isEditing ? <input className="w-full border border-white/10 p-3 rounded-xl text-sm bg-brand-slate text-white focus:ring-2 focus:ring-brand-purple" value={formData.website} onChange={e => setFormData({ ...formData, website: e.target.value })} /> : <a href={formData.website} target="_blank" className="text-sm text-brand-purple hover:text-white transition-colors font-bold flex items-center gap-1.5"><GlobeIcon /> {formData.website || 'Niet ingevuld'}</a>}
                                </div>
                            </div>
                        </div>
                    )}

                    {tab === 'research' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2">
                            {/* Research Data - Dark Mode */}
                            {getSpecificResult('researcher') ? (
                                <div className="space-y-6">
                                    <div className="bg-white/5 p-6 rounded-2xl border border-white/10 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]">
                                        <h4 className="text-[10px] font-black text-brand-purple uppercase tracking-widest mb-3 flex items-center gap-2"><BookIcon /> Onderzoek Samenvatting</h4>
                                        <p className="text-sm text-slate-300 leading-relaxed font-medium">
                                            {getSpecificResult('researcher')?.data.summary}
                                        </p>
                                    </div>
                                    <div className="space-y-3">
                                        <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Diepte Inzichten</h5>
                                        <ul className="space-y-2">
                                            {getSpecificResult('researcher')?.data.deepInsights?.map((insight: string, i: number) => (
                                                <li key={i} className="text-xs text-slate-300 flex gap-3 bg-brand-slate p-3 rounded-xl border border-white/5">
                                                    <span className="w-1.5 h-1.5 bg-brand-purple rounded-full mt-1.5 shrink-0 shadow-[0_0_8px_rgba(139,92,246,0.8)]"></span>
                                                    {insight}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-10 border-2 border-dashed border-white/10 rounded-2xl">
                                    <p className="text-xs text-slate-500">Nog geen onderzoek uitgevoerd.</p>
                                </div>
                            )}

                            {/* Concurrentie Analyse */}
                            {competitorResult && (
                                <div className="mt-8 pt-8 border-t border-white/10 space-y-6">
                                    <h4 className="text-[10px] font-black text-orange-400 uppercase tracking-widest flex items-center gap-2">
                                        <TargetIcon /> Concurrentie Landschap
                                    </h4>
                                    <div className="bg-orange-950/20 p-6 rounded-2xl border border-orange-500/20 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]">
                                        <div className="mb-4">
                                            <p className="text-[9px] font-black text-orange-500 uppercase tracking-widest mb-1">Marktpositie</p>
                                            <p className="text-xs font-bold text-slate-200">{competitorResult.data.marketPosition}</p>
                                        </div>
                                        <div className="space-y-2">
                                            <p className="text-[9px] font-black text-orange-500 uppercase tracking-widest">Top Concurrenten</p>
                                            {competitorResult.data.topCompetitors?.map((comp: any, idx: number) => (
                                                <div key={idx} className="bg-brand-slate p-3 rounded-xl border border-white/5 text-xs">
                                                    <span className="font-bold text-white block mb-1">{comp.name}</span>
                                                    <div className="flex gap-2 mt-2">
                                                        <span className="text-emerald-400 text-[10px] bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded flex-1">S: {comp.strength}</span>
                                                        <span className="text-rose-400 text-[10px] bg-rose-500/10 border border-rose-500/20 px-2 py-0.5 rounded flex-1">Z: {comp.weakness}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Strategy Data - Visual Canvas Approach */}
                            {getSpecificResult('strategy-agent') && (
                                <div className="mt-8 pt-8 border-t border-white/10 space-y-6">
                                    <div className="bg-emerald-950/20 p-6 rounded-2xl border border-emerald-500/20 relative overflow-hidden">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 blur-3xl rounded-full"></div>
                                        <h4 className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-3 flex items-center gap-2"><TargetIcon /> Strategie: {getSpecificResult('strategy-agent')?.data.strategyTitle}</h4>
                                        <p className="text-sm text-slate-200 leading-relaxed font-medium italic relative z-10">
                                            "{getSpecificResult('strategy-agent')?.data.approachAngle}"
                                        </p>
                                    </div>
                                    <div className="space-y-3">
                                        <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Key Pitch Points</h5>
                                        <div className="flex flex-wrap gap-2">
                                            {getSpecificResult('strategy-agent')?.data.keyPitchPoints?.map((p: string, i: number) => (
                                                <span key={i} className="text-[10px] bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 px-3 py-1.5 rounded-lg font-bold shadow-sm">{p}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === 'history' && (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center border-b border-white/10 pb-4">
                                <h3 className="font-black text-white text-[10px] uppercase tracking-[0.2em]">Geheugen & Strategie</h3>
                                <button onClick={loadMemory} className="text-slate-500 hover:text-white transition-colors" title="Ververs Geheugen">
                                    <RefreshIcon />
                                </button>
                            </div>

                            {/* PATTERNS SECTION */}
                            <div className="bg-brand-slate border border-white/5 rounded-xl p-5">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-xs font-bold text-white flex items-center gap-2">
                                        <PhoneIcon /> Call Analytics
                                    </h4>
                                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                        {callAnalytics.totalCalls} gesprekken
                                    </span>
                                </div>

                                {callAnalytics.totalCalls === 0 ? (
                                    <p className="text-xs text-slate-500 italic">Nog geen calls geanalyseerd voor dit dossier.</p>
                                ) : (
                                    <div className="space-y-5">
                                        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                                            <div className="rounded-xl border border-white/5 bg-white/5 p-3">
                                                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Totaal Calls</p>
                                                <p className="text-lg font-black text-white">{callAnalytics.totalCalls}</p>
                                            </div>
                                            <div className="rounded-xl border border-white/5 bg-white/5 p-3">
                                                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Met Transcript</p>
                                                <p className="text-lg font-black text-white">{callAnalytics.transcriptCoverage}</p>
                                            </div>
                                            <div className="rounded-xl border border-white/5 bg-white/5 p-3">
                                                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Gem. Score</p>
                                                <p className="text-lg font-black text-white">{callAnalytics.avgScore !== null ? `${callAnalytics.avgScore}/100` : 'n.v.t.'}</p>
                                            </div>
                                            <div className="rounded-xl border border-white/5 bg-white/5 p-3">
                                                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Laatste Uitkomst</p>
                                                <p className="text-sm font-black text-brand-purple">{formatOutcomeLabel(callAnalytics.lastCall?.details?.outcome)}</p>
                                            </div>
                                        </div>

                                        <div className="grid gap-4 xl:grid-cols-[1.3fr_1fr]">
                                            <div className="rounded-xl border border-white/5 bg-white/5 p-4">
                                                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-2">Gespreksbeeld</p>
                                                <div className="flex flex-wrap gap-2 text-[11px]">
                                                    <span className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-1.5 font-bold text-emerald-300">
                                                        Positief: {callAnalytics.sentiments.positive || 0}
                                                    </span>
                                                    <span className="rounded-lg border border-slate-500/20 bg-white/5 px-3 py-1.5 font-bold text-slate-300">
                                                        Neutraal: {callAnalytics.sentiments.neutral || 0}
                                                    </span>
                                                    <span className="rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-1.5 font-bold text-rose-300">
                                                        Negatief: {callAnalytics.sentiments.negative || 0}
                                                    </span>
                                                </div>
                                                <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                                                    {Object.entries(callAnalytics.outcomes).map(([label, count]) => (
                                                        <span key={label} className="rounded-lg border border-brand-purple/20 bg-brand-purple/10 px-3 py-1.5 font-bold text-brand-purple">
                                                            {label}: {count}
                                                        </span>
                                                    ))}
                                                </div>
                                                {callAnalytics.lastCall && (
                                                    <div className="mt-4 rounded-xl border border-white/5 bg-[#0b1120] p-4">
                                                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Laatste Analyse</p>
                                                        <p className="text-xs leading-6 text-slate-300">
                                                            {translateCallPhrase(callAnalytics.lastCall.details?.summary || callAnalytics.lastCall.summary)}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="rounded-xl border border-white/5 bg-white/5 p-4">
                                                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-2">Volgende Beste Actie</p>
                                                {callAnalytics.nextSteps.length > 0 ? (
                                                    <ul className="space-y-2 text-xs text-slate-300 leading-6">
                                                        {callAnalytics.nextSteps.map((item) => (
                                                            <li key={item.label} className="rounded-lg border border-white/5 bg-[#0b1120] px-3 py-2">
                                                                {translateCallPhrase(item.label)} <span className="text-slate-500">({item.count}x)</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-xs text-slate-500 italic">Nog geen concrete vervolgstappen vastgelegd.</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid gap-4 xl:grid-cols-3">
                                            <div className="rounded-xl border border-amber-500/15 bg-amber-500/5 p-4">
                                                <p className="text-[9px] font-black uppercase tracking-widest text-amber-300 mb-2">Terugkerende Bezwaren</p>
                                                {callAnalytics.objections.length > 0 ? (
                                                    <ul className="space-y-2 text-xs text-slate-300 leading-5">
                                                        {callAnalytics.objections.map((item) => (
                                                            <li key={item.label}>
                                                                {translateCallPhrase(item.label)} <span className="text-slate-500">({item.count}x)</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-xs text-slate-500 italic">Nog geen bezwaren verzameld.</p>
                                                )}
                                            </div>

                                            <div className="rounded-xl border border-emerald-500/15 bg-emerald-500/5 p-4">
                                                <p className="text-[9px] font-black uppercase tracking-widest text-emerald-300 mb-2">Werkende Hoeken</p>
                                                {callAnalytics.successfulAngles.length > 0 ? (
                                                    <ul className="space-y-2 text-xs text-slate-300 leading-5">
                                                        {callAnalytics.successfulAngles.map((item) => (
                                                            <li key={item.label}>
                                                                {translateCallPhrase(item.label)} <span className="text-slate-500">({item.count}x)</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-xs text-slate-500 italic">Nog geen herhaalbaar werkende invalshoeken.</p>
                                                )}
                                            </div>

                                            <div className="rounded-xl border border-brand-purple/15 bg-brand-purple/5 p-4">
                                                <p className="text-[9px] font-black uppercase tracking-widest text-brand-purple mb-2">Coach Focus</p>
                                                {callAnalytics.coachingNotes.length > 0 ? (
                                                    <ul className="space-y-2 text-xs text-slate-300 leading-5">
                                                        {callAnalytics.coachingNotes.map((item) => (
                                                            <li key={item.label}>
                                                                {translateCallPhrase(item.label)} <span className="text-slate-500">({item.count}x)</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-xs text-slate-500 italic">Nog geen coach-notes beschikbaar.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {patterns && (
                                <div className="bg-brand-slate border border-white/5 rounded-xl p-5 mb-8">
                                    <div className="flex justify-between items-center mb-4">
                                        <h4 className="text-xs font-bold text-white flex items-center gap-2">
                                            <LightbulbIcon /> Strategisch Geheugen
                                        </h4>
                                        {!isEditingPatterns ? (
                                            <button onClick={() => setIsEditingPatterns(true)} className="text-[10px] text-brand-purple font-bold hover:text-white transition-colors">Bewerken</button>
                                        ) : (
                                            <div className="flex gap-2">
                                                <button onClick={() => { setIsEditingPatterns(false); loadMemory(); }} className="text-[10px] text-slate-500 font-bold hover:text-white border border-white/10 px-2 py-0.5 rounded">Annuleren</button>
                                                <button onClick={handleSavePatterns} className="text-[10px] text-emerald-400 font-bold border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 rounded">Opslaan</button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="space-y-4">
                                        {/* Tone */}
                                        <div>
                                            <label className="block text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Voorkeurstoon</label>
                                            {isEditingPatterns ? (
                                                <select
                                                    className="w-full text-xs p-2 rounded border border-white/10 bg-brand-navy text-white focus:ring-brand-purple"
                                                    value={patterns.preferredTone}
                                                    onChange={e => setPatterns({ ...patterns, preferredTone: e.target.value })}
                                                >
                                                    <option value="professional">Professioneel</option>
                                                    <option value="casual">Informeel / Casual</option>
                                                    <option value="direct">Direct / To-the-point</option>
                                                    <option value="friendly">Vriendelijk / Warm</option>
                                                </select>
                                            ) : (
                                                <span className="text-xs font-bold text-slate-300 bg-white/5 px-2 py-1 rounded border border-white/5 inline-block capitalize">
                                                    {patterns.preferredTone}
                                                </span>
                                            )}
                                        </div>

                                        {/* Successful Angles */}
                                        <div>
                                            <label className="block text-[9px] font-black text-emerald-500 uppercase tracking-widest mb-1">Succesfactoren (Wat werkt?)</label>
                                            {isEditingPatterns ? (
                                                <textarea
                                                    className="w-full text-xs p-2 rounded border border-emerald-500/20 bg-emerald-500/5 text-white h-16 outline-none focus:ring-1 focus:ring-emerald-500"
                                                    value={patterns.successfulAngles.join('\n')}
                                                    onChange={e => setPatterns({ ...patterns, successfulAngles: e.target.value.split('\n').filter(x => x.trim()) })}
                                                    placeholder="Eén factor per regel..."
                                                />
                                            ) : (
                                                <ul className="list-disc list-inside text-xs text-slate-300 space-y-1">
                                                    {patterns.successfulAngles.length > 0 ? patterns.successfulAngles.map((a, i) => <li key={i}>{a}</li>) : <li className="text-slate-600 italic">Nog geen data.</li>}
                                                </ul>
                                            )}
                                        </div>

                                        {/* Failed Angles */}
                                        <div>
                                            <label className="block text-[9px] font-black text-rose-500 uppercase tracking-widest mb-1">Vermijden (Wat werkt niet?)</label>
                                            {isEditingPatterns ? (
                                                <textarea
                                                    className="w-full text-xs p-2 rounded border border-rose-500/20 bg-rose-500/5 text-white h-16 outline-none focus:ring-1 focus:ring-rose-500"
                                                    value={patterns.failedAngles.join('\n')}
                                                    onChange={e => setPatterns({ ...patterns, failedAngles: e.target.value.split('\n').filter(x => x.trim()) })}
                                                    placeholder="Eén factor per regel..."
                                                />
                                            ) : (
                                                <ul className="list-disc list-inside text-xs text-slate-300 space-y-1">
                                                    {patterns.failedAngles.length > 0 ? patterns.failedAngles.map((a, i) => <li key={i}>{a}</li>) : <li className="text-slate-600 italic">Nog geen data.</li>}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="border-t border-white/10 pt-6">
                                <h4 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4">Interactie Logboek</h4>
                                {history.length === 0 ? (
                                    <p className="text-center py-4 text-slate-500 text-sm">Geen interacties gevonden.</p>
                                ) : (
                                    <div className="space-y-6">
                                        {history.map((h) => (
                                            <InteractionItem
                                                key={h.id}
                                                interaction={h}
                                                onEdit={(id, text) => { setEditingInteraction(id); setEditInteractionData(text); }}
                                                isEditing={editingInteraction === h.id}
                                                editData={editInteractionData}
                                                setEditData={setEditInteractionData}
                                                onSave={handleSaveInteraction}
                                                onCancel={() => setEditingInteraction(null)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-4 bg-brand-slate border-t border-white/10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    <button
                        onClick={() => onRunAgent && onRunAgent('email-outreach', {
                            companyId: company.id,
                            companyName: company.name,
                            companySnapshot: company
                        })}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-white/5 hover:bg-white/10 hover:text-white text-slate-400 transition-all border border-white/5 group"
                    >
                        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shadow-sm mb-2 group-hover:scale-110 group-hover:bg-brand-purple group-hover:border-brand-purple transition-all"><MailIcon /></div>
                        <span className="text-[9px] font-bold uppercase tracking-widest">E-mail</span>
                    </button>

                    <button
                        onClick={() => window.open(`https://wa.me/${company.phone?.replace(/[^0-9]/g, '')}`, '_blank')}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-white/5 hover:bg-white/10 hover:text-emerald-400 text-slate-400 transition-all border border-white/5 group"
                    >
                        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shadow-sm mb-2 group-hover:scale-110 group-hover:bg-emerald-500 group-hover:text-white transition-all"><WhatsappIcon /></div>
                        <span className="text-[9px] font-bold uppercase tracking-widest">WhatsApp</span>
                    </button>

                    <button
                        onClick={() => onRunAgent && onRunAgent('researcher-competitor', { companyName: company.name, companyId: company.id, mode: 'competitor' })}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-white/5 hover:bg-white/10 hover:text-orange-400 text-slate-400 transition-all border border-white/5 group"
                    >
                        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shadow-sm mb-2 group-hover:scale-110 group-hover:bg-orange-500 group-hover:text-white transition-all"><TargetIcon /></div>
                        <span className="text-[9px] font-bold uppercase tracking-widest">Concurrentie</span>
                    </button>

                    <button
                        onClick={() => {
                            if (onRunAgent) {
                                onRunAgent('researcher', { companyName: company.name, companyId: company.id, mode: 'deep' });
                            }
                        }}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-white/5 hover:bg-white/10 hover:text-indigo-400 text-slate-400 transition-all border border-white/5 group"
                    >
                        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shadow-sm mb-2 group-hover:scale-110 group-hover:bg-indigo-500 group-hover:text-white transition-all"><SearchIcon /></div>
                        <span className="text-[9px] font-bold uppercase tracking-widest">Deep Dive</span>
                    </button>

                    <button
                        onClick={() => {
                            const research = researchResults.find(r => r.agentId === 'researcher' && (r.data.companyName === company.name || r.data.companyId === company.id));
                            if (onRunAgent) {
                                onRunAgent('strategy-agent', { researchData: research?.data || { companyId: company.id, companyName: company.name }, companyId: company.id, companyName: company.name });
                            }
                        }}
                        className="flex flex-col items-center justify-center p-3 rounded-xl bg-white/5 hover:bg-white/10 hover:text-emerald-400 text-slate-400 transition-all border border-white/5 group"
                    >
                        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shadow-sm mb-2 group-hover:scale-110 group-hover:bg-emerald-500 group-hover:text-white transition-all"><TargetIcon /></div>
                        <span className="text-[9px] font-bold uppercase tracking-widest">Strategie</span>
                    </button>

                    {!disableCallAgent && (
                        <button
                            onClick={() => onStartCall && onStartCall(company)}
                            className="flex flex-col items-center justify-center p-3 rounded-xl bg-white/5 hover:bg-white/10 hover:text-indigo-400 text-slate-400 transition-all border border-white/5 group"
                        >
                            <div className="w-8 h-8 rounded-full bg-brand-purple border border-brand-purple/50 flex items-center justify-center text-white shadow-[0_0_15px_rgba(139,92,246,0.5)] mb-2 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(139,92,246,0.8)] transition-all"><PhoneIcon /></div>
                            <span className="text-[9px] font-bold uppercase tracking-widest text-brand-purple group-hover:text-indigo-300">Live Gesprek</span>
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};

// Helper Component for Interaction Item to manage expanded state
const InteractionItem = ({ interaction, onEdit, isEditing, editData, setEditData, onSave, onCancel }: any) => {
    const [expanded, setExpanded] = useState(false);
    const h = interaction;
    const transcript = h.type === 'call' ? formatCallTranscript(h.details) : '';
    const analysis = h.type === 'call' ? formatCallAnalysis(h) : '';
    const learning = h.type === 'call' ? getCallLearningBlocks(h.details) : null;
    return (
        <div className="flex gap-4 relative pb-6 last:pb-0 group">
            <div className="absolute left-[13px] top-8 bottom-0 w-px bg-white/5 last:hidden"></div>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 z-10 text-[10px] shadow-sm ${h.type === 'email' ? 'bg-blue-600 text-white' : h.type === 'meeting' ? 'bg-purple-600 text-white' : 'bg-brand-purple text-white'}`}>
                {h.type === 'email' ? <MailIcon /> : h.type === 'meeting' ? <TargetIcon /> : <PhoneIcon />}
            </div>
            <div className="flex-1">
                <div className="flex justify-between items-start mb-1">
                    <h4 className="text-sm font-bold text-slate-200 capitalize">{h.type} <span className="text-slate-500 font-normal text-xs">({h.direction})</span></h4>
                    <span className="text-[10px] font-medium text-slate-500">{new Date(h.timestamp).toLocaleDateString('nl-NL')}</span>
                </div>

                {isEditing ? (
                    <div className="mt-2">
                        <textarea
                            className="w-full border border-white/10 p-3 rounded-lg text-xs bg-brand-slate text-white focus:ring-2 focus:ring-brand-purple outline-none h-24 mb-2"
                            value={editData}
                            onChange={(e: any) => setEditData(e.target.value)}
                        />
                        <div className="flex gap-2 justify-end">
                            <button onClick={onCancel} className="text-[10px] px-3 py-1.5 rounded-md font-bold text-slate-400 hover:text-white">Annuleren</button>
                            <button onClick={() => onSave(h.id)} className="text-[10px] px-3 py-1.5 rounded-md font-bold text-white bg-brand-purple hover:bg-violet-500 shadow-sm shadow-brand-purple/20">Opslaan</button>
                        </div>
                    </div>
                ) : (
                    <div className="group/content relative">
                        <p className="text-xs text-slate-300 leading-relaxed bg-white/5 p-3 rounded-lg border border-white/5">
                            {h.summary}
                        </p>

                        {h.details && (
                            <div className="mt-2">
                                <button
                                    onClick={() => setExpanded(!expanded)}
                                    className="text-[10px] font-bold text-brand-purple hover:text-white flex items-center gap-1 transition-colors"
                                >
                                    {expanded ? 'Verberg details' : 'Toon transcriptie / details'}
                                </button>
                                {expanded && (
                                    <div className="mt-2 p-3 bg-[#080d1a] rounded-lg text-xs text-slate-300 whitespace-pre-wrap border border-white/5 overflow-x-auto space-y-3">
                                        {h.type === 'call' ? (
                                            <>
                                                {transcript && (
                                                    <div className="space-y-1">
                                                        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">Volledige Transcriptie</p>
                                                        <div className="rounded-lg border border-white/5 bg-white/[0.03] p-3 font-mono text-[11px] leading-6 text-slate-300 whitespace-pre-wrap">
                                                            {transcript}
                                                        </div>
                                                    </div>
                                                )}
                                                {analysis && (
                                                    <div className="space-y-1">
                                                        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">AI Analyse</p>
                                                        <div className="rounded-lg border border-white/5 bg-white/[0.03] p-3 leading-6 text-slate-300 whitespace-pre-wrap">
                                                            {analysis}
                                                        </div>
                                                    </div>
                                                )}
                                                {learning && (learning.successfulAngles.length > 0 || learning.failedAngles.length > 0 || learning.commonObjections.length > 0 || learning.coachingNotes.length > 0 || learning.factualNotes.length > 0) && (
                                                    <div className="grid gap-3 md:grid-cols-2">
                                                        {learning.successfulAngles.length > 0 && (
                                                            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
                                                                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300">Wat Werkte</p>
                                                                <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] leading-5 text-slate-300">
                                                                    {learning.successfulAngles.map((item, index) => <li key={`success-${index}`}>{item}</li>)}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        {learning.failedAngles.length > 0 && (
                                                            <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-3">
                                                                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-300">Wat Niet Werkte</p>
                                                                <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] leading-5 text-slate-300">
                                                                    {learning.failedAngles.map((item, index) => <li key={`failed-${index}`}>{item}</li>)}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        {learning.commonObjections.length > 0 && (
                                                            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                                                                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">Bezwaren Voor Leren</p>
                                                                <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] leading-5 text-slate-300">
                                                                    {learning.commonObjections.map((item, index) => <li key={`objection-${index}`}>{item}</li>)}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        {learning.coachingNotes.length > 0 && (
                                                            <div className="rounded-lg border border-brand-purple/20 bg-brand-purple/5 p-3">
                                                                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand-purple">Coach Notes</p>
                                                                <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] leading-5 text-slate-300">
                                                                    {learning.coachingNotes.map((item, index) => <li key={`coach-${index}`}>{item}</li>)}
                                                                </ul>
                                                            </div>
                                                        )}
                                                        {learning.factualNotes.length > 0 && (
                                                            <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-3 md:col-span-2">
                                                                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">Feiten Uit Gesprek</p>
                                                                <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] leading-5 text-slate-300">
                                                                    {learning.factualNotes.map((item, index) => <li key={`fact-${index}`}>{item}</li>)}
                                                                </ul>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {!transcript && !analysis && (
                                                    <div className="font-mono text-slate-400">
                                                        {typeof h.details === 'string' ? h.details : JSON.stringify(h.details, null, 2)}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="font-mono text-slate-400">
                                                {typeof h.details === 'string' ? h.details : JSON.stringify(h.details, null, 2)}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <button
                            onClick={() => onEdit(h.id, h.summary)}
                            className="absolute top-2 right-2 p-1.5 text-slate-500 hover:text-white bg-brand-slate rounded-md shadow-sm opacity-0 group-hover/content:opacity-100 transition-all border border-white/10"
                        >
                            <PencilIcon />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
